import { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useRouter } from 'next/router';

import fetchMoreListings from '@/utilities/fetchMoreListings';

import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';

/**
 * Custom hook to handle infinite scroll functionality
 * Extracts scroll logic to reduce cognitive complexity of main component
 */
const useInfiniteScroll = ({
    rootMargin = 5000,
    isMaxActiveResults,
    listings,
    query,
    featureFlags,
    inventoryImageUrl,
    sort,
    brand,
    channel,
    vhrProviders,
    relevanceConfig,
    zip,
}) => {
    const dispatch = useDispatch();
    const router = useRouter();

    const [infiniteScrollButtonPressed, setInfiniteScrollButtonPressed] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [inViewKey, setInViewKey] = useState(0);

    /**
     * Handle fetching more data for infinite scroll
     */
    const handleFetchMoreData = useCallback(async () => {
        if (isLoadingMore || isMaxActiveResults) return; // Prevent duplicate requests

        try {
            setIsLoadingMore(true);

            // Call the enhanced fetchMoreListings utility that now fetches both listings and spotlights
            await fetchMoreListings({
                listings,
                query,
                dispatch,
                router,
                featureFlags,
                inventoryImageUrl,
                sort,
                brand,
                channel,
                vhrProviders,
                relevanceConfig,
                onComplete: () => {
                    setInfiniteScrollButtonPressed(true);
                },
                zip,
            });

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error fetching more results:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [
        isLoadingMore,
        isMaxActiveResults,
        listings,
        query,
        dispatch,
        router,
        featureFlags,
        inventoryImageUrl,
        sort,
        brand,
        channel,
        vhrProviders,
        relevanceConfig,
        zip,
    ]);

    /**
     * Handle InView change for automatic infinite scroll
     */
    const handleInViewChange = useCallback((inView) => {
        // Only trigger if we're actively scrolling and the component comes into view
        if (inView && window?.scrollY > 0 && !isLoadingMore) {
            handleFetchMoreData();
        }
    }, [handleFetchMoreData, isLoadingMore]);

    /**
     * Setup event listeners for navigation and visibility changes
     */
    useEffect(() => {
        const handleNavigation = () => {
            // On any pageloads after the first one skip rendering the lazy listings.  This helps a lot with CLS on
            // filter changes and back button that goes from VDP back to SRP.
            dispatch(srpActiveInteractionDuck.creators.setKeys({
                disableLazyListings: true,
            }));
            setInViewKey((prev) => prev + 1);
        };

        const handleVisibilityChange = () => {
            // Reset when page becomes visible again (handles browser back/forward)
            if (document.visibilityState === 'visible') {
                setInViewKey((prev) => prev + 1);
            }
        };

        const handlePageShow = (event) => {
            // Handle browser back/forward navigation with page cache
            if (event.persisted) {
                setInViewKey((prev) => prev + 1);
            }
        };

        router.events.on('routeChangeComplete', handleNavigation);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);

        return () => {
            router.events.off('routeChangeComplete', handleNavigation);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [router, dispatch]);

    return {
        infiniteScrollButtonPressed,
        isLoadingMore,
        inViewKey,
        handleFetchMoreData,
        handleInViewChange,
        scrollConfig: {
            rootMargin: `${rootMargin}px`,
            threshold: [0, 0.1, 0.5, 1],
            triggerOnce: false,
        },
    };
};

export default useInfiniteScroll;
